import { useState, useEffect } from 'react';

interface TypewriterTextProps {
  text: string;
  speed?: number;
  delay?: number;
  className?: string;
  showCursor?: boolean;
  onComplete?: () => void;
}

export function TypewriterText({ 
  text, 
  speed = 80, 
  delay = 0,
  className = '',
  showCursor = true,
  onComplete
}: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);
    
    const startTimeout = setTimeout(() => {
      setIsTyping(true);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, delay]);

  useEffect(() => {
    if (!isTyping) return;

    if (displayedText.length < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, displayedText.length + 1));
      }, speed);
      return () => clearTimeout(timeout);
    } else {
      setIsComplete(true);
      setIsTyping(false);
      onComplete?.();
    }
  }, [displayedText, text, speed, isTyping, onComplete]);

  return (
    <span className={className}>
      {displayedText}
      {showCursor && !isComplete && (
        <span className="inline-block w-[3px] h-[1em] bg-primary ml-1 animate-[blink_1s_step-end_infinite] align-middle" />
      )}
      {showCursor && isComplete && (
        <span className="inline-block w-[3px] h-[1em] bg-primary/50 ml-1 animate-[blink_1s_step-end_infinite] align-middle" />
      )}
    </span>
  );
}

// Multi-line typewriter that types lines sequentially
interface TypewriterLinesProps {
  lines: string[];
  speed?: number;
  lineDelay?: number;
  className?: string;
  lineClassName?: string;
}

export function TypewriterLines({
  lines,
  speed = 60,
  lineDelay = 500,
  className = '',
  lineClassName = ''
}: TypewriterLinesProps) {
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [completedLines, setCompletedLines] = useState<string[]>([]);

  const handleLineComplete = () => {
    if (currentLineIndex < lines.length - 1) {
      setCompletedLines(prev => [...prev, lines[currentLineIndex]]);
      setTimeout(() => {
        setCurrentLineIndex(prev => prev + 1);
      }, lineDelay);
    }
  };

  return (
    <div className={className}>
      {completedLines.map((line, index) => (
        <div key={index} className={lineClassName}>
          {line}
        </div>
      ))}
      {currentLineIndex < lines.length && (
        <TypewriterText
          text={lines[currentLineIndex]}
          speed={speed}
          className={lineClassName}
          onComplete={handleLineComplete}
          showCursor={currentLineIndex === lines.length - 1}
        />
      )}
    </div>
  );
}